/**
 * Clerk Auth Provider
 *
 * Replaces Firebase AuthProvider with Clerk authentication.
 * Maintains the same interface for minimal migration impact.
 *
 * BUG-001 FIX: Now fetches tenant from database when localStorage is empty,
 * preventing the onboarding loop for returning users.
 */

import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { ClerkProvider, useUser, useAuth, useClerk } from '@clerk/clerk-react';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface TenantApiResponse {
  success: boolean;
  data?: {
    tenantId: string | null;
    companyName: string | null;
    slug: string | null;
    isSuperAdmin?: boolean;
  };
  error?: string;
}

// Get Clerk publishable key from environment
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable');
}

// ============================================
// Auth Context Interface (compatible with old AuthProvider)
// ============================================

interface AuthContextType {
  // Loading state
  isLoading: boolean;

  // Auth state
  isAuthenticated: boolean;
  user: ClerkUser | null;
  userId: string | null;
  tenantId: string | null;
  isSuperAdmin: boolean;

  // Auth methods (Clerk handles these via components, but we expose for compatibility)
  signOut: () => Promise<void>;

  // Tenant management
  setTenantId: (id: string) => void;
}

interface ClerkUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  imageUrl: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================
// Inner Provider (must be inside ClerkProvider)
// ============================================

function AuthContextProvider({ children }: { children: ReactNode }) {
  const { user, isLoaded: isUserLoaded } = useUser();
  const { isLoaded: isAuthLoaded, isSignedIn, getToken } = useAuth();
  const { signOut: clerkSignOut } = useClerk();

  const [tenantId, setTenantId] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoadingTenant, setIsLoadingTenant] = useState(true);
  const fetchAttemptedRef = useRef(false);

  const isLoading = !isUserLoaded || !isAuthLoaded || isLoadingTenant;

  // Fetch tenant from database
  const fetchTenantFromDatabase = useCallback(async (): Promise<{ tenantId: string | null; isSuperAdmin: boolean }> => {
    try {
      const token = await getToken();
      if (!token) {
        console.warn('[ClerkAuthProvider] No token available for tenant fetch');
        return { tenantId: null, isSuperAdmin: false };
      }

      const response = await fetch(`${API_BASE_URL}/users/me/tenant`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.warn('[ClerkAuthProvider] Failed to fetch tenant:', response.status);
        return { tenantId: null, isSuperAdmin: false };
      }

      const json: TenantApiResponse = await response.json();

      if (json.success && json.data) {
        console.log('[ClerkAuthProvider] Fetched tenant from database:', json.data.tenantId);
        return {
          tenantId: json.data.tenantId,
          isSuperAdmin: json.data.isSuperAdmin ?? false,
        };
      }

      return { tenantId: null, isSuperAdmin: false };
    } catch (error) {
      console.error('[ClerkAuthProvider] Error fetching tenant from database:', error);
      return { tenantId: null, isSuperAdmin: false };
    }
  }, [getToken]);

  // Load tenant ID from localStorage or database when user is authenticated
  useEffect(() => {
    if (!isAuthLoaded || !isUserLoaded) {
      return;
    }

    // Reset fetch flag when user changes
    if (!isSignedIn || !user) {
      fetchAttemptedRef.current = false;
      setTenantId(null);
      setIsSuperAdmin(false);
      setIsLoadingTenant(false);
      return;
    }

    // Check for pending invite from signup
    const inviteTenantId = sessionStorage.getItem('invite_tenant_id');
    if (inviteTenantId && !fetchAttemptedRef.current) {
      fetchAttemptedRef.current = true;
      console.log('[ClerkAuthProvider] Processing invite for tenant:', inviteTenantId);

      const processInvite = async () => {
        try {
          const token = await getToken();
          if (!token) {
            console.warn('[ClerkAuthProvider] No token for invite acceptance');
            setIsLoadingTenant(false);
            return;
          }

          const response = await fetch(`${API_BASE_URL}/admin/invite/accept`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ tenantId: inviteTenantId }),
          });

          const result = await response.json();

          if (response.ok && result.data?.tenantId) {
            console.log('[ClerkAuthProvider] Invite accepted, tenant:', result.data.tenantId);
            sessionStorage.removeItem('invite_tenant_id');
            localStorage.setItem(`tenant_${user.id}`, result.data.tenantId);
            setTenantId(result.data.tenantId);
            // Fetch isSuperAdmin status
            const adminResult = await fetchTenantFromDatabase();
            setIsSuperAdmin(adminResult.isSuperAdmin);
          } else {
            console.warn('[ClerkAuthProvider] Invite acceptance failed:', result.error);
            sessionStorage.removeItem('invite_tenant_id');
          }
        } catch (error) {
          console.error('[ClerkAuthProvider] Error accepting invite:', error);
          sessionStorage.removeItem('invite_tenant_id');
        } finally {
          setIsLoadingTenant(false);
        }
      };

      processInvite();
      return;
    }

    // Check localStorage first for fast tenant ID restore
    const storedTenantId = localStorage.getItem(`tenant_${user.id}`);

    if (storedTenantId && !fetchAttemptedRef.current) {
      console.log('[ClerkAuthProvider] Using tenant from localStorage:', storedTenantId);
      setTenantId(storedTenantId);

      // Fetch isSuperAdmin flag before marking loading complete
      // This ensures SuperAdminRoute has the correct value before rendering
      fetchAttemptedRef.current = true;
      fetchTenantFromDatabase()
        .then((result) => {
          setIsSuperAdmin(result.isSuperAdmin);
        })
        .catch((error) => {
          console.error('[ClerkAuthProvider] Failed to fetch isSuperAdmin:', error);
        })
        .finally(() => {
          setIsLoadingTenant(false);
        });
      return;
    }

    // BUG-001 FIX: If localStorage is empty, fetch from database
    if (!fetchAttemptedRef.current) {
      fetchAttemptedRef.current = true;
      console.log('[ClerkAuthProvider] localStorage empty, fetching from database...');

      fetchTenantFromDatabase()
        .then((result) => {
          setIsSuperAdmin(result.isSuperAdmin);
          if (result.tenantId) {
            // Cache in localStorage for future use
            localStorage.setItem(`tenant_${user.id}`, result.tenantId);
            setTenantId(result.tenantId);
          } else {
            // User genuinely has no tenant - needs onboarding
            console.log('[ClerkAuthProvider] No tenant found in database, user needs onboarding');
            setTenantId(null);
          }
        })
        .catch((error) => {
          // Handle network failures - allow user to retry or proceed to onboarding
          console.error('[ClerkAuthProvider] Failed to fetch tenant:', error);
          setTenantId(null);
        })
        .finally(() => {
          setIsLoadingTenant(false);
        });
    }
  }, [isAuthLoaded, isUserLoaded, isSignedIn, user, fetchTenantFromDatabase]);

  // Sync Sentry user context when auth state changes
  useEffect(() => {
    if (isSignedIn && user) {
      setSentryUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        tenantId,
      });
    } else {
      clearSentryUser();
    }
  }, [isSignedIn, user, tenantId]);

  const handleSetTenantId = (id: string) => {
    setTenantId(id);
    if (user) {
      localStorage.setItem(`tenant_${user.id}`, id);
    }
  };

  const handleSignOut = async () => {
    if (user) {
      localStorage.removeItem(`tenant_${user.id}`);
    }
    setTenantId(null);
    setIsSuperAdmin(false);
    await clerkSignOut();
  };

  // Map Clerk user to our interface
  const mappedUser: ClerkUser | null = user
    ? {
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress || null,
        firstName: user.firstName,
        lastName: user.lastName,
        imageUrl: user.imageUrl,
      }
    : null;

  const value: AuthContextType = {
    isLoading,
    isAuthenticated: !!isSignedIn,
    user: mappedUser,
    userId: user?.id || null,
    tenantId,
    isSuperAdmin,
    signOut: handleSignOut,
    setTenantId: handleSetTenantId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================
// Main Provider (wraps ClerkProvider)
// ============================================

export function ClerkAuthProvider({ children }: { children: ReactNode }) {
  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      afterSignOutUrl="/sign-in"
    >
      <AuthContextProvider>{children}</AuthContextProvider>
    </ClerkProvider>
  );
}

// ============================================
// Hooks (compatible with old AuthProvider)
// ============================================

/**
 * Hook to access the auth context
 */
export function useAuthContext(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within a ClerkAuthProvider');
  }
  return context;
}

/**
 * Convenience hook to get just the tenant ID
 * Throws if not authenticated
 */
export function useRequiredTenantId(): string {
  const { tenantId, isAuthenticated } = useAuthContext();
  if (!isAuthenticated || !tenantId) {
    throw new Error('User must be authenticated with a tenant');
  }
  return tenantId;
}
